import React, { FC, useEffect, useMemo } from "react";

import {
  ActionValue,
  BusinessFilterNature,
  createFolderBusinessFilter,
  currentFolderRootBreadcrumbContext,
  DataViewKey,
  getBusinessFilterFirstValue,
  getParameterByName,
  KpkFilterValue,
  MediaField,
  NO_FACETS_COUNT,
  SpecificDataviewType,
  useCurrentUser,
  useShareToken,
} from "@keepeek/commons";
import { KButton, KFSearch } from "@keepeek/refront-components";
import { PageOverrideKey } from "@keepeek/refront-customers";
import { Box, LinearProgress } from "@mui/material";
import { toNumber } from "lodash";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../components/layout/adminMenu";
import { PagesPath } from "../containers/App/utils";
import EditableSanitizeHtml from "../containers/FrontEdit/EditableSanitizeHtml";
import DefaultLayout from "../containers/Layout";
import { thumbnailGeneratorModalState } from "../containers/ThumbnailGeneratorModal/atom";
import FolderBreadcrumb from "../containers/widgets/FolderBreadcrumb";
import KFooterWidget from "../containers/widgets/KFooter";
import { LEGACY_FOOTER_ID } from "../containers/widgets/KFooter/utils";
import KTemplates from "../containers/widgets/KTemplates";
import { GetCustomerAugmentedComponent, getCustomerOverrideComponent } from "../lib/overrides";
import { Dataview } from "../models/configuration/components/dataview";
import { Footer } from "../models/configuration/components/footer";
import { FooterWidget } from "../models/configuration/components/footerWidget";
import { ThumbnailGenerator } from "../models/configuration/components/thumbnailGenerator";
import { Type } from "../models/configuration/definitions/widget";
import { Global, Visibility } from "../models/configuration/global";
import { Theme } from "../models/configuration/theme";
import { useBackContext } from "../providers/backContext/hooks";
import { useActiveBusinessFiltersSync } from "../providers/businessFilter/hooks/useActiveBusinessFiltersSync";
import { useActiveFiltersFromURL } from "../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { useLBFInit } from "../providers/businessFilter/hooks/useLBF";
import { encodeToLightBusinessFilters } from "../providers/businessFilter/utils/lbf/encoding";
import { useConfiguration, useKTemplatesConf } from "../providers/config/hooks";
import { useHeaderMetas } from "../providers/config/hooks/useHeaderMetas";
import { configSectionPageSearchSelector } from "../providers/config/selectors";
import {
  convertDisplayMode,
  isPageVisible,
  KTemplatesTemplatesName,
} from "../providers/config/utils";
import { contactSheetsSelectionSelector } from "../providers/contactSheet/selectors/selections";
import { frontEditState } from "../providers/frontEdit/atoms/frontEdit";
import useFrontEditModal from "../providers/frontEdit/hooks/useFrontEditModal";
import { useCurrentLocale } from "../providers/i18n/hooks";

const Search: FC<React.PropsWithChildren<unknown>> = function Search() {
  // Sync active business
  useActiveBusinessFiltersSync(DataViewKey.SEARCH);
  useLBFInit();
  const router = useRouter();
  const configGlobal = useConfiguration<Global>(CONFIGURATION_SECTION_PATH.GLOBAL);
  const { t } = useTranslation();

  const { setIsOpen, setCurrentEditKeys } = useFrontEditModal();

  useHeaderMetas({ title: t("page.search.title") as string });

  useEffect(() => {
    if (!isPageVisible(configGlobal, Visibility.Search)) {
      router.push(PagesPath.ERROR_404);
    }
  }, [configGlobal, router]);

  const folderId = getParameterByName("folder");
  if (folderId) {
    const bf = createFolderBusinessFilter(
      toNumber(folderId),
      "",
      t("business-filter.advanced.folder.label"),
    );
    const lbf = encodeToLightBusinessFilters([bf]);
    router.push(`${PagesPath.SEARCH_PAGE}?lbf=${lbf}`);
  }

  const { activeFilters, rawURLFilters, isFolderSearch } = useActiveFiltersFromURL();
  const setThumbnailGeneratorModal = useSetRecoilState(thumbnailGeneratorModalState);
  const handleElementActionClick = (elementId: number, action: ActionValue) => {
    switch (action) {
      case ActionValue.SHOW_THUMBNAIL_GENERATOR:
        setThumbnailGeneratorModal(elementId);
        break;
    }
  };

  const { goToPageWithBackContext } = useBackContext();
  const filterFolderId: KpkFilterValue | undefined = getBusinessFilterFirstValue(
    activeFilters,
    BusinessFilterNature.ADVANCED,
    MediaField.FOLDER_ID,
  );

  const themeConfiguration = useConfiguration<Theme>(CONFIGURATION_SECTION_PATH.THEME);

  const { widgetsConfiguration, templates, loading } = useKTemplatesConf({
    templatePagePosition: CONFIGURATION_SECTION_PATH.PAGES_SEARCH,
  });

  const frontEdit = useRecoilValue(frontEditState);
  const {
    widgetsConfiguration: widgetsConfigurationFooter,
    templates: templatesFooter,
    loading: loadingFooter,
  } = useKTemplatesConf({
    templatePagePosition: CONFIGURATION_SECTION_PATH.PAGES_SEARCH,
    templatesName: KTemplatesTemplatesName.TemplatesFooter,
  });

  // Management of backward compatibility
  const footerConfiguration = useConfiguration<Footer>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_FOOTER,
  );
  let widgetsConfigurationFooterRetro = false;
  if ((!templatesFooter || templatesFooter.length === 0) && footerConfiguration) {
    widgetsConfigurationFooterRetro = true;
  }

  const initFolderRootIdFromURL = useMemo(
    () => (getParameterByName("fc") ? toNumber(getParameterByName("fc")) : undefined),
    // We want it to be recalculated on every query change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query],
  );

  const currentFolderId = useRecoilValue(currentFolderRootBreadcrumbContext);
  const {
    displayCount,
    displayModes,
    maxElementsPerPage,
    displayMode: defaultDisplayMode,
  } = useConfiguration<Dataview>(CONFIGURATION_SECTION_PATH.COMPONENTS_DATAVIEW) ?? {
    displayCount: undefined,
    displayModes: undefined,
  };
  const setSelectionToContactSheets = useSetRecoilState(contactSheetsSelectionSelector);

  const { shareTokenDownloadAuthorized } = useShareToken(router.pathname, router.query.tokenId);
  const dataViewConfig = useConfiguration<Dataview>(CONFIGURATION_SECTION_PATH.COMPONENTS_DATAVIEW);
  const thumbnailGeneratorConfig = useConfiguration<ThumbnailGenerator>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_THUMBNAIL_GENERATOR,
  );

  const { hasContactSheets } = useCurrentUser();
  const contactSheetsEnabled = (dataViewConfig?.contactSheets && hasContactSheets) || false;
  const currentLocale = useCurrentLocale();

  const { query } = useRouter();
  const noFacetsCount = NO_FACETS_COUNT in query;

  const { actionButtonsColor } = useRecoilValue(configSectionPageSearchSelector) ?? {};

  const dataviewActionList = [
    ActionValue.SHOW_ATTACHMENT,
    ActionValue.ADD_TO_BASKET,
    ActionValue.DOWNLOAD,
    ActionValue.SHARE,
  ];
  if (thumbnailGeneratorConfig?.enabled) {
    dataviewActionList.push(ActionValue.SHOW_THUMBNAIL_GENERATOR);
  }

  return (
    <DefaultLayout>
      <KFSearch
        EditableSanitizeHtml={EditableSanitizeHtml}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          flexGrow: 1,
        }}
        actionBarActionList={[
          ActionValue.ADD_TO_BASKET,
          ActionValue.DOWNLOAD,
          ActionValue.CONTACT_SHEETS,
          ActionValue.REMOVE_FROM_BASKET,
        ]}
        actionButtonsColor={actionButtonsColor}
        isActionBarCountDisplayed={displayCount !== undefined ? displayCount : true}
        isSelectionEnabled={true}
        isActionBarSticky={true}
        isFacetCountEnabled={!noFacetsCount}
        isContactSheetsEnabled={contactSheetsEnabled}
        isShareTokenDownloadAuthorized={shareTokenDownloadAuthorized}
        isFolderSearch={isFolderSearch}
        isDataviewNavigationAllowed={true}
        dataviewMaxElementsPerPage={maxElementsPerPage}
        actionBarDisplayModes={
          displayModes?.map((mode) => convertDisplayMode(mode)) || [
            SpecificDataviewType.MOSAIC,
            SpecificDataviewType.MASONRY,
          ]
        }
        actionBarDefaultDisplayMode={
          defaultDisplayMode ? convertDisplayMode(defaultDisplayMode) : undefined
        }
        actionBarSetSelectionToContactSheets={setSelectionToContactSheets}
        headerInputSearchComponent={null}
        breadcrumbComponent={
          <FolderBreadcrumb
            type="NOT_A_WIDGET_YET"
            folderId={filterFolderId?.id}
            home={() => {
              router.push(PagesPath.HOME_PAGE);
            }}
            initFolderRootId={currentFolderId ?? initFolderRootIdFromURL}
          />
        }
        locale={currentLocale || "FR"}
        dataviewGoToPageWithBackContext={(context) => {
          if (context && context.sourcePage) {
            context.sourcePage.query = rawURLFilters;
          }
          goToPageWithBackContext(context);
        }}
        footerComponent={
          (loadingFooter && <LinearProgress />) ||
          (!loadingFooter && !widgetsConfigurationFooterRetro && (
            <KTemplates
              templatesConfiguration={templatesFooter || []}
              widgetsConfiguration={widgetsConfigurationFooter || null}
              themeConfiguration={themeConfiguration || null}
            />
          )) ||
          (!loadingFooter &&
            widgetsConfigurationFooterRetro &&
            !frontEdit &&
            footerConfiguration && (
              <KFooterWidget
                type={Type.Footer}
                widgetId={LEGACY_FOOTER_ID}
                footerConfiguration={footerConfiguration as FooterWidget}
              />
            ))
        }
        KIcon={KIcon}
        GetCustomerAugmentedComponent={GetCustomerAugmentedComponent}
        businessFiltersHeader={
          frontEdit ? (
            <Box sx={{ padding: (theme) => theme.spacing(2) }}>
              <KButton
                variant="outlined"
                fullWidth
                paletteColor={{
                  main: "#116ddf",
                  contrastText: "",
                  dark: "",
                  light: "",
                }}
                onClick={() => {
                  setCurrentEditKeys([
                    { configSection: CONFIGURATION_SECTION_PATH.COMPONENTS_BUSINESS_FILTER },
                  ]);
                  setIsOpen(true);
                }}
              >
                {t("business-filter.front-edit.filters.edit.label")}
              </KButton>
            </Box>
          ) : null
        }
        onElementActionClick={handleElementActionClick}
        dataviewActionList={dataviewActionList}
      >
        {loading && <LinearProgress />}
        {!loading && (
          <KTemplates
            templatesConfiguration={templates || []}
            widgetsConfiguration={widgetsConfiguration || null}
            themeConfiguration={themeConfiguration || null}
          />
        )}
      </KFSearch>
    </DefaultLayout>
  );
};
export default getCustomerOverrideComponent(PageOverrideKey.Search, Search);
